import type { Recording } from "../api/parse.ts";
import { formatDuration, formatFileSize, formatSessionWhen } from "./sessionWhen.ts";

export const MISSING = "—";

export const RECORDING_DETAIL_FIELD_KEYS = [
  "recordingId",
  "deviceName",
  "connectedUser",
  "deviceId",
  "sessionId",
  "startedAt",
  "endedAt",
  "duration",
  "size",
  "contentType",
  "sha256",
  "bytes",
  "retentionUntil",
  "createdAt",
] as const;

export type RecordingDetailFieldKey = (typeof RECORDING_DETAIL_FIELD_KEYS)[number];

export type RecordingDetailField = {
  key: RecordingDetailFieldKey;
  label: string;
  value: string;
};

const SECRET_LABEL = /\b(token|secret|password|credential|sas|sig)\b/i;

export function dialogShowsSecretLikeLabel(text: string): boolean {
  return SECRET_LABEL.test(text);
}

function text(value: string | null | undefined): string {
  if (value === null || value === undefined) return MISSING;
  const trimmed = value.trim();
  return trimmed.length === 0 ? MISSING : trimmed;
}

export function recordingDetailFields(recording: Recording, timeZone?: string): RecordingDetailField[] {
  const rows: RecordingDetailField[] = [];
  for (const key of RECORDING_DETAIL_FIELD_KEYS) {
    switch (key) {
      case "recordingId":
        rows.push({ key, label: "Recording id", value: recording.recordingId });
        break;
      case "deviceName":
        rows.push({ key, label: "Device", value: recording.deviceName });
        break;
      case "connectedUser":
        rows.push({ key, label: "User", value: recording.connectedUser });
        break;
      case "deviceId":
        rows.push({ key, label: "Device id", value: recording.deviceId });
        break;
      case "sessionId":
        rows.push({ key, label: "Session id", value: recording.sessionId });
        break;
      case "startedAt":
        rows.push({ key, label: "Start", value: formatSessionWhen(recording.startedAt, undefined, timeZone) });
        break;
      case "endedAt":
        rows.push({ key, label: "End", value: recording.endedAt ? formatSessionWhen(recording.endedAt, undefined, timeZone) : "In progress" });
        break;
      case "duration":
        rows.push({ key, label: "Duration", value: formatDuration(recording.startedAt, recording.endedAt) });
        break;
      case "size":
        rows.push({ key, label: "Size", value: formatFileSize(recording.bytes) });
        break;
      case "contentType":
        rows.push({ key, label: "Content type", value: text(recording.contentType) });
        break;
      case "sha256":
        rows.push({ key, label: "SHA-256", value: recording.sha256 });
        break;
      case "bytes":
        rows.push({ key, label: "Bytes", value: String(recording.bytes) });
        break;
      case "retentionUntil":
        rows.push({ key, label: "Retention until", value: formatSessionWhen(recording.retentionUntil, undefined, timeZone) });
        break;
      case "createdAt":
        rows.push({ key, label: "Created", value: formatSessionWhen(recording.createdAt, undefined, timeZone) });
        break;
      default: {
        const _never: never = key;
        throw new Error(`Unhandled recording detail field ${_never}`);
      }
    }
  }
  return rows;
}
